"use client";

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useStore, getters } from '@/store';
import { endSession } from '@/api/auth';
import logo from '@/assets/logo.png';
import { 
  CreditCard, 
  LogOut, 
  LayoutDashboard, 
  ShieldCheck, 
  Search,
  LogIn,
  User,
  Heart,
  Info,       

  FileText,   
  Shield,     
  RefreshCcw,
  HelpCircle,
  Mail,
  Truck,
  ChevronDown, 
  ChevronUp,
  MoreHorizontal,
  Scale
} from 'lucide-react';

export function Header() {
  const { state, dispatch } = useStore();
  const router = useRouter();
  const pathname = usePathname();
  
  // Menu States
  const [profileOpen, setProfileOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAuthenticated = getters.isAuthenticated(state);
  const isAdmin = getters.isAdmin(state);
  const user = getters.currentUser(state);

  const handleLogout = async () => {
    // Revoke server-side first; clearing only local state would leave the
    // session token usable until it expired on its own.
    await endSession();
    dispatch({ type: 'LOGOUT' });
    router.push('/');
    setProfileOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
        setLegalOpen(false); 
        setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getInitials = (name: string) => {
    if (!name) return 'G'; 
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isHome = pathname === '/';

  return (
    <header 
      className={`
        top-0 z-50 w-full transition-all duration-300
        ${isHome 
          ? 'fixed bg-transparent border-none' 
          : 'sticky bg-background/80 backdrop-blur-xl border-b border-border/40'
        }
      `}
    >
      <div className="container flex h-16 items-center justify-between">
        
        {/* LEFT: Logo */}
        <Link href="/" className="flex items-center gap-2 group">
          
          {/* ✅ UPDATED IMAGE STYLES:
              - h-8 md:h-10: Smaller height on mobile, regular on desktop
              - w-auto: Maintains aspect ratio (prevents squishing)
              - object-left: Aligns logo to the left
          */}
          <img 
            src={logo.src} 
            alt="CardBox Logo" 
            className="h-8 md:h-10 w-auto object-contain object-left transition-transform group-hover:scale-105" 
          />
        </Link>

        {/* RIGHT: Avatar + Dropdown */}
        <div className="flex items-center gap-4">
          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => setProfileOpen(!profileOpen)}
              className={`
                relative flex h-10 w-10 items-center justify-center rounded-full 
                border shadow-lg transition-all duration-300 hover:scale-110 
                ${isHome 
                  ? 'bg-white/80 backdrop-blur-md border-white/20 hover:bg-white' 
                  : 'bg-background border-border hover:border-primary/50'
                }
                ${profileOpen ? 'scale-110 ring-2 ring-primary/20 ring-offset-2' : ''}
              `}
            >
              <span className="text-sm font-bold text-primary">
                {getInitials(user?.full_name || '')}
              </span>
            </button>

            {/* MAIN DROPDOWN MENU */}
            <div 
              className={`
                absolute right-0 top-14 w-64 origin-top-right rounded-xl border border-border/50 
                bg-background/95 backdrop-blur-xl p-2 shadow-xl transition-all duration-200 ease-out max-h-[85vh] overflow-y-auto scrollbar-none
                ${profileOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}
              `}
            >
              {/* 1. User Info (Auth only) */}
              {isAuthenticated && (
                <div className="px-3 py-2 mb-2 border-b border-border/50">
                  <p className="text-sm font-bold text-foreground truncate">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {(user as any)?.email || (user as any)?.phone_number || 'Logged in user'}
                  </p>
                </div>
              )}

              <div className="space-y-1">
                
                {/* 2. Primary Action */}
                <Link 
                  href="/providers" 
                  onClick={() => setProfileOpen(false)}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors text-primary"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Find Providers
                </Link>

                <div className="h-px bg-border/50 my-1" />

                {/* 3. Auth / Guest Links (PRIORITY AT TOP) */}
                {isAuthenticated ? (
                  <>
                    {isAdmin && (
                      <Link 
                        href="/admin" 
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors"
                      >
                        <ShieldCheck className="mr-2 h-4 w-4 text-amber-500" />
                        Admin Panel
                      </Link>
                    )}
                    
                    <Link 
                      href="/dashboard" 
                      onClick={() => setProfileOpen(false)}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors"
                    >
                      <LayoutDashboard className="mr-2 h-4 w-4 text-blue-500" />
                      Dashboard
                    </Link>

                    <Link 
                      href="/profile" 
                      onClick={() => setProfileOpen(false)}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors"
                    >
                      <User className="mr-2 h-4 w-4 text-green-500" />
                      Profile
                    </Link>

                    <Link 
                      href="/wishlist" 
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg hover:bg-accent transition-colors"
                    >
                      <Heart className="mr-2 h-4 w-4 text-red-500 fill-red-500" />
                      <span>Wishlist</span>
                    </Link>

                    <div className="h-px bg-border/50 my-1" />

                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setProfileOpen(false)}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors"
                    >
                      <LogIn className="mr-2 h-4 w-4" />
                      Sign In
                    </Link>

                    <Link
                      href="/register"
                      onClick={() => setProfileOpen(false)}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors"
                    >
                      <CreditCard className="mr-2 h-4 w-4 text-primary" />
                      Get Started
                    </Link>
                  </>
                )}

                <div className="h-px bg-border/50 my-1" />

                {/* 4. "MORE" DROPDOWN (Now at bottom) */}
                <div>
                  <button 
                    onClick={() => setMoreOpen(!moreOpen)}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <div className="flex items-center">
                      <MoreHorizontal className="mr-2 h-4 w-4" />
                      More Info
                    </div>
                    {moreOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                  
                  {moreOpen && (
                    <div className="ml-2 pl-2 border-l-2 border-border/50 space-y-1 my-1 animate-in slide-in-from-top-1 fade-in duration-200">
                      <Link 
                        href="/about-us" 
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <Info className="mr-2 h-3.5 w-3.5" />
                        About Us
                      </Link>
                      <Link 
                        href="/faq" 
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <HelpCircle className="mr-2 h-3.5 w-3.5" />
                        FAQ
                      </Link>
                      <Link 
                        href="/contact-us" 
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <Mail className="mr-2 h-3.5 w-3.5" />
                        Contact Us
                      </Link>
                    </div>
                  )}
                </div>
                
                {/* 5. "LEGAL" DROPDOWN (At very bottom) */}
                <div>
                  <button 
                    onClick={() => setLegalOpen(!legalOpen)}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <div className="flex items-center">
                      <Scale className="mr-2 h-4 w-4" />
                      Legal Info
                    </div>
                    {legalOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                  
                  {legalOpen && (
                    <div className="ml-2 pl-2 border-l-2 border-border/50 space-y-1 my-1 animate-in slide-in-from-top-1 fade-in duration-200">
                      <Link
                        href="/terms-and-conditions"
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <FileText className="mr-2 h-3.5 w-3.5" />
                        Terms & Conditions
                      </Link>

                      <Link
                        href="/privacy-policy"
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <Shield className="mr-2 h-3.5 w-3.5" />
                        Privacy Policy
                      </Link>

                      <Link
                        href="/refund-policy"
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                        Refund Policy
                      </Link>

                      <Link
                        href="/shipping-policy"
                        onClick={() => setProfileOpen(false)}
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <Truck className="mr-2 h-3.5 w-3.5" />
                        Shipping Policy
                      </Link>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}